<template>
  <!-- styled version of https://tailgrids.com/components/datepicker -->
  <section class="inline-flex border font-mono text-sm uppercase">
    <div class="container">
      <div ref="datepickerContainerRef" class="">
        <div class="relative">
          <input
            id="datepicker"
            v-model="selectedDate"
            name="date"
            type="hidden"
            placeholder="Pick a date"
            class="border-stroke text-dark focus:border-primary dark:border-dark-3 dark:bg-dark-2 h-12 w-full appearance-none rounded-lg border bg-white pr-4 pl-12 outline-none dark:text-white"
            readonly
            @click="handleToggleCalendar"
          />
        </div>
        <div
          v-if="isCalendarOpen"
          id="datepicker-container"
          ref="datepickerContainer"
          class="inline-flex flex-col gap-3 p-4 sm:p-[30px]"
        >
          <div class="flex items-center justify-between">
            <button
              id="prevMonth"
              type="button"
              class="flex h-[38px] w-[38px] cursor-pointer items-center justify-center sm:h-[46px] sm:w-[46px]"
              @click="handlePrevMonth"
            >
              <IconsArrowE class="size-6" />
            </button>
            <span id="currentMonth">
              {{
                currentDate.toLocaleDateString('en-US', {
                  month: 'long',
                  year: 'numeric',
                })
              }}
            </span>
            <button
              id="nextMonth"
              type="button"
              class="flex h-[38px] w-[38px] cursor-pointer items-center justify-center sm:h-[46px] sm:w-[46px]"
              @click="handleNextMonth"
            >
              <IconsArrowW class="size-6" />
            </button>
          </div>
          <div id="days-of-week" class="grid grid-cols-7 gap-2">
            <span
              class="flex h-[38px] w-[38px] items-center justify-center sm:h-[46px] sm:w-[47px]"
            >
              Mo
            </span>

            <span
              class="flex h-[38px] w-[38px] items-center justify-center sm:h-[46px] sm:w-[47px]"
            >
              Tu
            </span>

            <span
              class="flex h-[38px] w-[38px] items-center justify-center sm:h-[46px] sm:w-[47px]"
            >
              We
            </span>

            <span
              class="flex h-[38px] w-[38px] items-center justify-center sm:h-[46px] sm:w-[47px]"
            >
              Th
            </span>

            <span
              class="flex h-[38px] w-[38px] items-center justify-center sm:h-[46px] sm:w-[47px]"
            >
              Fr
            </span>

            <span
              class="flex h-[38px] w-[38px] items-center justify-center sm:h-[46px] sm:w-[47px]"
            >
              Sa
            </span>

            <span
              class="flex h-[38px] w-[38px] items-center justify-center sm:h-[46px] sm:w-[47px]"
            >
              Su
            </span>
          </div>

          <div id="days-container" ref="daysContainer" class="grid grid-cols-7 gap-2">
            <!-- Days will be rendered here -->
            <div
              v-for="day in days"
              :key="day || `empty-${Math.random()}`"
              class="flex h-[38px] w-[38px] items-center justify-center border sm:h-[46px] sm:w-[47px]"
              :class="{
                'text-dark hover:border-stroke hover:bg-gray-2 cursor-pointer border-black/50':
                  day !== null && !isPastDate(day),
                'text-neon-yellow bg-black': isSelected(day),
                'pointer-events-none border-transparent': day === null || isPastDate(day),
                'hover:bg-black/20': day !== null && !isSelected(day) && !isPastDate(day),
                'opacity-30': isPastDate(day), // Optional: faded style for past dates
              }"
              @click="day !== null && !isPastDate(day) ? selectDate(day) : null"
            >
              {{ day }}
            </div>
          </div>

          <!-- <div class="flex items-center space-x-3 pt-4 sm:space-x-5">
            <button
              type="button"
              id="cancelBtn"
              class="bg-dark hover:bg-opacity-90 flex h-[50px] w-full items-center justify-center rounded-md text-base font-medium text-white"
              @click="handleCancel"
            >
              Remove
            </button>
            <button
              type="button"
              id="applyBtn"
              class="bg-primary hover:bg-blue-dark flex h-[50px] w-full items-center justify-center rounded-md text-base font-medium text-white"
              @click="handleApply"
            >
              Done
            </button>
          </div> -->
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'

const currentDate = ref(new Date())
const selectedDate = ref(null)
const isCalendarOpen = ref(true)
const daysContainerRef = ref(null)
const datepickerContainerRef = ref(null)

// Compute the days for the current month
const days = computed(() => {
  const year = currentDate.value.getFullYear()
  const month = currentDate.value.getMonth()
  const firstDayOfMonth = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()

  // Convert Sunday (0) to 6, and other days to day-1 to make Monday (1) the first day (0)
  const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1

  // Create empty placeholders for days from previous month
  let daysArray = Array(adjustedFirstDay).fill(null)
  // Add the days of the current month
  daysArray = [...daysArray, ...Array.from({ length: daysInMonth }, (_, i) => i + 1)]

  return daysArray
})

// Watch for changes and re-render the calendar
watch([currentDate, isCalendarOpen], async () => {
  await nextTick() // Ensure DOM updates are applied
  renderCalendar()
})

const renderCalendar = () => {
  // Ensure daysContainerRef is attached
  if (daysContainerRef.value) {
    // Your calendar rendering logic
  }
}

const handlePrevMonth = () => {
  currentDate.value = new Date(currentDate.value.setMonth(currentDate.value.getMonth() - 1))
}

const handleNextMonth = () => {
  currentDate.value = new Date(currentDate.value.setMonth(currentDate.value.getMonth() + 1))
}

const _handleApply = () => {
  if (selectedDate.value) {
    isCalendarOpen.value = false
  }
}

const _handleCancel = () => {
  selectedDate.value = null
  isCalendarOpen.value = false
}

const handleToggleCalendar = () => {
  isCalendarOpen.value = !isCalendarOpen.value
}

const isSelected = (day) => {
  if (day === null || !selectedDate.value) return false
  const selectedDateObj = new Date(selectedDate.value)
  return (
    selectedDateObj.getDate() === day &&
    selectedDateObj.getMonth() === currentDate.value.getMonth() &&
    selectedDateObj.getFullYear() === currentDate.value.getFullYear()
  )
}

const selectDate = (day) => {
  const month = currentDate.value.getMonth() + 1
  const year = currentDate.value.getFullYear()
  selectedDate.value = `${month}/${day}/${year}`
}

onMounted(() => {
  // Initial setup only - no document event listeners
})

// Not including any onUnmounted handler for click outside

const isPastDate = (day) => {
  if (day === null) return false

  const now = new Date()
  const targetDate = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth(), day)

  // Reset time components for accurate comparison
  now.setHours(0, 0, 0, 0)
  targetDate.setHours(0, 0, 0, 0)

  return targetDate < now
}
</script>
